/**
 * <license header>
 */

import {
  WebhookActionOperation,
  WebhookActionSuccessResponse,
  WebhookActionExceptionResponse,
  WebhookActionAddResponse,
  WebhookActionReplaceResponse,
  WebhookActionRemoveResponse,
} from './types';

/**
 * WebhookActionResponse - Factory class for creating Adobe Commerce webhook responses
 *
 * This class provides static methods to create properly formatted responses for
 * Adobe Commerce webhooks. These responses can be used to signal success, report
 * exceptions, or modify webhook payloads by adding, replacing, or removing data.
 *
 * All methods return strongly-typed response objects that conform to Adobe Commerce's
 * webhook response specification.
 *
 * @example
 * ```typescript
 * // Return a success response
 * return WebhookActionResponse.success();
 *
 * // Report an exception
 * return WebhookActionResponse.exception('ValidationException', 'Invalid product data');
 *
 * // Add new data to the payload
 * return WebhookActionResponse.add('order.metadata', { processed: true });
 *
 * // Modify existing data
 * return WebhookActionResponse.replace('product.price', 29.99);
 *
 * // Remove data from the payload
 * return WebhookActionResponse.remove('customer.internal_notes');
 * ```
 *
 * @see https://developer.adobe.com/commerce/extensibility/webhooks/
 */
class WebhookActionResponse {
  /**
   * Creates a success response indicating the webhook was processed successfully.
   *
   * Use this method when the webhook has been processed without errors and
   * no modifications to the payload are needed.
   *
   * @returns A success response object
   *
   * @example
   * ```typescript
   * const handler = WebhookAction.execute('process-order', [], [], async (params) => {
   *   // Process the order...
   *   await processOrder(params.order);
   *
   *   // Return success
   *   return {
   *     statusCode: 200,
   *     body: WebhookActionResponse.success()
   *   };
   * });
   * ```
   */
  static success(): WebhookActionSuccessResponse {
    return {
      op: WebhookActionOperation.SUCCESS,
    };
  }

  /**
   * Creates an exception response to report an error during webhook processing.
   *
   * Use this method to notify Adobe Commerce that an error occurred while
   * processing the webhook. This helps with debugging and error tracking.
   *
   * @param message - Optional error message describing what went wrong
   * @param exceptionClass - Optional exception class name for categorization (e.g., 'Magento\\Framework\\Exception\\LocalizedException')
   * @returns An exception response object
   *
   * @example
   * ```typescript
   * const handler = WebhookAction.execute('validate-product', [], [], async (params) => {
   *   const product = await findProduct(params.sku);
   *
   *   if (!product) {
   *     return {
   *       statusCode: 404,
   *       body: WebhookActionResponse.exception(
   *         `Product with SKU ${params.sku} not found`,
   *         'Magento\\Framework\\Exception\\NoSuchEntityException'
   *       )
   *     };
   *   }
   *
   *   return { statusCode: 200, body: WebhookActionResponse.success() };
   * });
   * ```
   */
  static exception(message?: string, exceptionClass?: string): WebhookActionExceptionResponse {
    const response: WebhookActionExceptionResponse = {
      op: WebhookActionOperation.EXCEPTION,
    };

    if (message !== undefined) {
      response.message = message;
    }
    if (exceptionClass !== undefined) {
      response.class = exceptionClass;
    }

    return response;
  }

  /**
   * Creates a response to add new data to the webhook payload.
   *
   * Use this method to inject additional data into the webhook payload that
   * will be processed by Adobe Commerce. The data is added at the specified
   * path using dot notation.
   *
   * @param path - Dot-notation path where the value should be added (e.g., 'order.items', 'customer.addresses')
   * @param value - The value to add at the specified path
   * @param instance - Optional instance identifier for tracking or reference purposes
   * @returns An add response object
   *
   * @example
   * ```typescript
   * const handler = WebhookAction.execute('enrich-order', [], [], async (params) => {
   *   // Add loyalty points to the order
   *   return {
   *     statusCode: 200,
   *     body: WebhookActionResponse.add(
   *       'order.loyalty',
   *       { points: 150, tier: 'gold' },
   *       params.order.id
   *     )
   *   };
   * });
   * ```
   */
  static add(path: string, value: any, instance?: string): WebhookActionAddResponse {
    const response: WebhookActionAddResponse = {
      op: WebhookActionOperation.ADD,
      path: path,
      value: value,
    };

    if (instance !== undefined) {
      response.instance = instance;
    }

    return response;
  }

  /**
   * Creates a response to replace existing data in the webhook payload.
   *
   * Use this method to modify existing fields in the webhook payload.
   * The existing value at the specified path will be replaced with the new value.
   *
   * @param path - Dot-notation path to the field that should be replaced (e.g., 'product.price', 'order.status')
   * @param value - The new value to replace the existing value
   * @param instance - Optional instance identifier for tracking or reference purposes
   * @returns A replace response object
   *
   * @example
   * ```typescript
   * const handler = WebhookAction.execute('adjust-price', [], [], async (params) => {
   *   // Apply dynamic pricing
   *   const newPrice = await calculateDiscountedPrice(params.product.price);
   *
   *   return {
   *     statusCode: 200,
   *     body: WebhookActionResponse.replace(
   *       'product.price',
   *       newPrice,
   *       params.product.id
   *     )
   *   };
   * });
   * ```
   */
  static replace(path: string, value: any, instance?: string): WebhookActionReplaceResponse {
    const response: WebhookActionReplaceResponse = {
      op: WebhookActionOperation.REPLACE,
      path: path,
      value: value,
    };

    if (instance !== undefined) {
      response.instance = instance;
    }

    return response;
  }

  /**
   * Creates a response to remove data from the webhook payload.
   *
   * Use this method to remove fields from the webhook payload before it's
   * processed by Adobe Commerce. This is useful for filtering sensitive data
   * or removing unnecessary information.
   *
   * @param path - Dot-notation path to the field that should be removed (e.g., 'items.0', 'customer.internal_notes')
   * @returns A remove response object
   *
   * @example
   * ```typescript
   * const handler = WebhookAction.execute('sanitize-customer', [], [], async (params) => {
   *   // Remove internal notes before processing
   *   return {
   *     statusCode: 200,
   *     body: WebhookActionResponse.remove('customer.internal_notes')
   *   };
   * });
   * ```
   *
   * @example
   * ```typescript
   * // Remove an item from an array
   * return {
   *   statusCode: 200,
   *   body: WebhookActionResponse.remove('order.items.2')
   * };
   * ```
   */
  static remove(path: string): WebhookActionRemoveResponse {
    return {
      op: WebhookActionOperation.REMOVE,
      path: path,
    };
  }
}

export default WebhookActionResponse;
